import { _isNotBlank } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import { type BaseValidator } from 'handsontable-v16/validators';
import { type CellProperties, type CellValue } from '@/support/hot-v16.types';

export const InVivoValidatorField = Symbol('in-vivo-validator');
export type InVivoValidatorResult = { valid: true } | { valid: false; message: string };
export type InVivoValidator = (value: CellValue, columnSettings: Record<string, any>) => Promise<InVivoValidatorResult>;
export type HybridValidator = BaseValidator & { [InVivoValidatorField]: InVivoValidator };
export const isHybridValidator = (validator: BaseValidator): validator is HybridValidator =>
  Object.prototype.hasOwnProperty.call(validator, InVivoValidatorField) &&
  typeof (validator as HybridValidator)[InVivoValidatorField] === 'function';
export const fromInVivoValidator = (
  inVivoValidator: InVivoValidator
): BaseValidator & { [InVivoValidatorField]: InVivoValidator } => {
  const validator = function (this: CellProperties, value: CellValue, callback: (valid: boolean) => void) {
    inVivoValidator(value, this)
      .then((result) => callback(result.valid))
      .catch(() => callback(false));
  };
  return Object.assign(validator, { [InVivoValidatorField]: inVivoValidator });
};

export const textMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;
  const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && stringValue.length > 250) {
    return { valid: false, message: 'Must have a max length of 250 characters' };
  }

  return { valid: true };
};

export const dateMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;
  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && !DateUtils.isValidDate(stringValue)) {
    return { valid: false, message: 'Must be a valid date' };
  }

  return { valid: true };
};

export const numberMetadataInVivoValidator: InVivoValidator = async (
  value: CellValue,
  columnSettings: Record<string, any>
) => {
  const required = columnSettings.required === true;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    return { valid: true };
  }

  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue && !Number.isFinite(Number(stringValue))) {
    return { valid: false, message: 'Must be a valid number' };
  }

  return { valid: true };
};
